import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail, isUpgradeRequired } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { track } from "@/lib/analytics";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Plus,
  Pill,
  Trash2,
  Sparkles,
  Loader2,
  ClipboardList,
  ChevronRight,
  Leaf,
} from "lucide-react";
import DrugAutocomplete from "@/components/DrugAutocomplete";
import { useProfile } from "@/context/ProfileContext";

const LOADING_STEPS = [
  "Cross-referencing drug databases…",
  "Checking CYP450 liver enzymes…",
  "Evaluating supplement & food conflicts…",
  "Building your safety profile…",
];

export default function Dashboard() {
  const { user } = useAuth();
  const { activeId } = useProfile();
  const navigate = useNavigate();
  const [meds, setMeds] = useState([]);
  const [lifestyle, setLifestyle] = useState({
    alcohol: "none",
    caffeine: "none",
    smoking: "none",
    grapefruit: false,
    high_potassium_diet: false,
    notes: "",
  });
  const [reports, setReports] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ name: "", category: "Prescription", dosage: "", frequency: "", notes: "" });
  const [analyzing, setAnalyzing] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!analyzing) return;
    const iv = setInterval(() => setStep((s) => (s + 1) % LOADING_STEPS.length), 1400);
    return () => clearInterval(iv);
  }, [analyzing]);

  const load = async () => {
    try {
      const [m, l, r] = await Promise.all([
        api.get("/medications"),
        api.get("/lifestyle"),
        api.get("/reports"),
      ]);
      setMeds(m.data);
      setLifestyle(l.data);
      setReports(r.data);
    } catch (e) {
      toast.error("Could not load your data.");
    }
  };
  useEffect(() => {
    if (!activeId) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  const addMed = async () => {
    if (!form.name.trim()) return toast.error("Name is required.");
    try {
      const { data } = await api.post("/medications", form);
      setMeds((prev) => [...prev, data]);
      track("Medication Added", { props: { category: form.category } });
      setForm({ name: "", category: "Prescription", dosage: "", frequency: "", notes: "" });
      setAddOpen(false);
      toast.success(`Added ${data.name}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const deleteMed = async (id) => {
    try {
      await api.delete(`/medications/${id}`);
      setMeds((prev) => prev.filter((m) => m.id !== id));
    } catch (e) {
      toast.error("Delete failed.");
    }
  };

  const saveLifestyle = async (next) => {
    setLifestyle(next);
    try {
      await api.put("/lifestyle", next);
    } catch (_e) {}
  };

  const analyze = async () => {
    if (meds.length === 0) {
      toast.error("Add at least one medication or supplement.");
      return;
    }
    setAnalyzing(true);
    setStep(0);
    track("Analysis Started", { props: { med_count: meds.length } });
    try {
      const { data } = await api.post("/analyze", {
        medications: meds.map(({ name, category, dosage, frequency, notes }) => ({
          name,
          category,
          dosage,
          frequency,
          notes,
        })),
        lifestyle,
      });
      track("Analysis Completed", {
        props: { harmony_score: data?.summary?.harmony_score ?? null, med_count: meds.length },
      });
      toast.success("Report ready.");
      navigate(`/reports/${data.id}`);
    } catch (e) {
      if (isUpgradeRequired(e)) {
        toast.info("Free plan limit reached. Redirecting to upgrade…");
        setTimeout(() => navigate("/pricing"), 900);
      } else {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Analysis failed.");
      }
    } finally {
      setAnalyzing(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12 fade-up">
      <div className="mb-8">
        <p className="text-xs uppercase tracking-widest font-semibold" style={{ color: "#5C6B66" }}>
          Welcome{user?.name ? `, ${user.name}` : ""}
        </p>
        <h1 className="mt-1 font-serif-display text-3xl sm:text-5xl font-semibold" style={{ color: "#1C2826" }}>
          Your regimen at a glance
        </h1>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* MEDICATIONS COLUMN */}
        <div className="lg:col-span-2 space-y-6">
          <div className="card-warm p-6">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-xl grid place-items-center" style={{ backgroundColor: "#E8F0EC" }}>
                  <Pill size={18} style={{ color: "#2D5A4C" }} />
                </div>
                <h2 className="font-serif-display text-2xl font-medium" style={{ color: "#1C2826" }}>
                  Medications &amp; supplements
                </h2>
              </div>
              <Button
                data-testid="open-add-medication-btn"
                onClick={() => setAddOpen(true)}
                className="btn-brand rounded-full h-10 px-4"
              >
                <Plus size={16} className="mr-1" /> Add
              </Button>
            </div>

            {meds.length === 0 ? (
              <div className="text-sm rounded-xl p-6 text-center" style={{ background: "#F4EFE6", color: "#5C6B66" }}>
                Add each medication, supplement, or herbal product you take. Include dosage
                and frequency for a sharper analysis.
              </div>
            ) : (
              <ul className="space-y-3" data-testid="medications-list">
                {meds.map((m) => (
                  <li
                    key={m.id}
                    className="flex items-start justify-between gap-3 p-4 rounded-2xl border"
                    style={{ borderColor: "#E5DEC9", background: "#FFFFFF" }}
                  >
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold" style={{ color: "#1C2826" }}>
                          {m.name}
                        </span>
                        <Badge
                          className="rounded-full text-[10px] uppercase tracking-wider"
                          style={{ background: "#E8F0EC", color: "#23473C", border: "none" }}
                        >
                          {m.category}
                        </Badge>
                      </div>
                      <p className="mt-1 text-sm" style={{ color: "#5C6B66" }}>
                        {[m.dosage, m.frequency].filter(Boolean).join(" · ")}
                      </p>
                      {m.notes ? (
                        <p className="mt-1 text-xs italic" style={{ color: "#5C6B66" }}>
                          {m.notes}
                        </p>
                      ) : null}
                    </div>
                    <button
                      data-testid={`delete-medication-${m.id}`}
                      onClick={() => deleteMed(m.id)}
                      aria-label={`Delete ${m.name}`}
                      className="p-2 rounded-full hover:bg-[#FDF0EA] transition-colors"
                    >
                      <Trash2 size={16} style={{ color: "#BF5832" }} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* LIFESTYLE */}
          <div className="card-warm p-6">
            <div className="flex items-center gap-2 mb-5">
              <div className="w-9 h-9 rounded-xl grid place-items-center" style={{ backgroundColor: "#FDF0EA" }}>
                <Leaf size={18} style={{ color: "#BF5832" }} />
              </div>
              <h2 className="font-serif-display text-2xl font-medium" style={{ color: "#1C2826" }}>
                Food &amp; lifestyle
              </h2>
            </div>

            <div className="grid sm:grid-cols-3 gap-4">
              {[
                { key: "alcohol", label: "Alcohol", options: ["none", "occasional", "daily"] },
                { key: "caffeine", label: "Caffeine", options: ["none", "occasional", "daily"] },
                { key: "smoking", label: "Smoking", options: ["none", "occasional", "daily"] },
              ].map((f) => (
                <div key={f.key}>
                  <Label>{f.label}</Label>
                  <Select
                    value={lifestyle[f.key]}
                    onValueChange={(v) => saveLifestyle({ ...lifestyle, [f.key]: v })}
                  >
                    <SelectTrigger data-testid={`lifestyle-${f.key}-trigger`} className="mt-1.5 h-11 rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {f.options.map((o) => (
                        <SelectItem key={o} value={o}>
                          {o.charAt(0).toUpperCase() + o.slice(1)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>

            <div className="grid sm:grid-cols-2 gap-4 mt-5">
              <div
                className="flex items-center justify-between p-3 rounded-xl border"
                style={{ borderColor: "#E5DEC9", background: "#FAF7F2" }}
              >
                <div>
                  <p className="text-sm font-semibold" style={{ color: "#1C2826" }}>
                    Grapefruit consumption
                  </p>
                  <p className="text-xs" style={{ color: "#5C6B66" }}>
                    Affects CYP3A4 metabolism
                  </p>
                </div>
                <Switch
                  data-testid="lifestyle-grapefruit-switch"
                  checked={lifestyle.grapefruit}
                  onCheckedChange={(v) => saveLifestyle({ ...lifestyle, grapefruit: v })}
                />
              </div>
              <div
                className="flex items-center justify-between p-3 rounded-xl border"
                style={{ borderColor: "#E5DEC9", background: "#FAF7F2" }}
              >
                <div>
                  <p className="text-sm font-semibold" style={{ color: "#1C2826" }}>
                    High-potassium diet
                  </p>
                  <p className="text-xs" style={{ color: "#5C6B66" }}>
                    Bananas, avocados, coconut water
                  </p>
                </div>
                <Switch
                  data-testid="lifestyle-potassium-switch"
                  checked={lifestyle.high_potassium_diet}
                  onCheckedChange={(v) => saveLifestyle({ ...lifestyle, high_potassium_diet: v })}
                />
              </div>
            </div>

            <div className="mt-4">
              <Label htmlFor="ls-notes">Other notes (optional)</Label>
              <Textarea
                id="ls-notes"
                data-testid="lifestyle-notes"
                value={lifestyle.notes || ""}
                onChange={(e) => setLifestyle({ ...lifestyle, notes: e.target.value })}
                onBlur={() => saveLifestyle(lifestyle)}
                className="mt-1.5 rounded-xl"
                placeholder="e.g., pregnancy, kidney condition, allergies…"
              />
            </div>
          </div>
        </div>

        {/* SIDE COLUMN */}
        <div className="space-y-6">
          <div className="card-warm p-6" style={{ background: "linear-gradient(160deg, #2D5A4C 0%, #23473C 100%)" }}>
            <div className="flex items-center gap-2 mb-3">
              <Sparkles size={20} color="#F4EFE6" />
              <h3 className="font-serif-display text-2xl font-medium" style={{ color: "#FAF7F2" }}>
                Run analysis
              </h3>
            </div>
            <p className="text-sm mb-5" style={{ color: "#E8F0EC" }}>
              Harmonix will produce a full interaction &amp; symptom report powered by GPT
              5.6 Terra clinical reasoning.
            </p>
            <Button
              data-testid="analyze-interactions-btn"
              onClick={analyze}
              disabled={analyzing || meds.length === 0}
              className="w-full h-12 rounded-full text-sm font-semibold btn-terracotta"
            >
              {analyzing ? (
                <>
                  <Loader2 size={16} className="animate-spin mr-2" /> Analyzing…
                </>
              ) : (
                <>
                  Analyze my regimen <ChevronRight size={16} className="ml-1" />
                </>
              )}
            </Button>
            {analyzing && (
              <div className="mt-5 space-y-2" data-testid="analyze-loading-steps">
                {LOADING_STEPS.map((s, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs" style={{ color: "#E8F0EC" }}>
                    <span
                      className={`w-2 h-2 rounded-full ${i === step ? "pulse-dot" : ""}`}
                      style={{ background: i <= step ? "#D96B43" : "#5C6B66" }}
                    />
                    {s}
                  </div>
                ))}
              </div>
            )}
            <p className="mt-4 text-[11px] leading-relaxed" style={{ color: "#E8F0EC" }}>
              Every report includes a medical disclaimer notice.
            </p>
          </div>

          <div className="card-warm p-6">
            <div className="flex items-center gap-2 mb-4">
              <ClipboardList size={18} style={{ color: "#2D5A4C" }} />
              <h3 className="font-serif-display text-xl font-medium" style={{ color: "#1C2826" }}>
                Past reports
              </h3>
            </div>
            {reports.length === 0 ? (
              <p className="text-sm" style={{ color: "#5C6B66" }}>
                Your first analysis will appear here.
              </p>
            ) : (
              <ul className="space-y-2" data-testid="reports-history">
                {reports.map((r) => (
                  <li key={r.id}>
                    <button
                      data-testid={`open-report-${r.id}`}
                      onClick={() => navigate(`/reports/${r.id}`)}
                      className="w-full text-left p-3 rounded-xl border hover:-translate-y-0.5 transition-transform"
                      style={{ borderColor: "#E5DEC9", background: "#FAF7F2" }}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold" style={{ color: "#1C2826" }}>
                          {new Date(r.created_at).toLocaleString()}
                        </span>
                        <span
                          className="text-xs font-bold px-2 py-0.5 rounded-full"
                          style={{
                            background: "#E8F0EC",
                            color: "#23473C",
                          }}
                        >
                          {r.harmony_score ?? "–"}/100
                        </span>
                      </div>
                      <p className="mt-1 text-xs" style={{ color: "#5C6B66" }}>
                        {r.med_count} item{r.med_count === 1 ? "" : "s"}
                      </p>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* ADD MED DIALOG */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="rounded-2xl max-w-md" data-testid="add-medication-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl">Add to regimen</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name</Label>
              <div className="mt-1.5">
                <DrugAutocomplete
                  value={form.name}
                  onChange={(v) => setForm({ ...form, name: v })}
                  placeholder="e.g. Sertraline, Vitamin D, St. John's Wort"
                />
              </div>
            </div>
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="add-med-category-trigger" className="mt-1.5 h-11 rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {["Prescription", "OTC", "Supplement", "Herbal"].map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Dosage</Label>
                <Input
                  data-testid="add-med-dosage-input"
                  value={form.dosage}
                  onChange={(e) => setForm({ ...form, dosage: e.target.value })}
                  className="mt-1.5 h-11 rounded-xl"
                  placeholder="e.g. 50mg"
                />
              </div>
              <div>
                <Label>Frequency</Label>
                <Input
                  data-testid="add-med-frequency-input"
                  value={form.frequency}
                  onChange={(e) => setForm({ ...form, frequency: e.target.value })}
                  className="mt-1.5 h-11 rounded-xl"
                  placeholder="once daily"
                />
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                data-testid="add-med-notes-input"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="mt-1.5 rounded-xl"
                placeholder="Optional context"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setAddOpen(false)}
              data-testid="add-med-cancel-btn"
              className="rounded-full"
            >
              Cancel
            </Button>
            <Button onClick={addMed} className="btn-brand rounded-full" data-testid="add-med-save-btn">
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
